/* global nexstar */

import clamp from 'utils/clamp';
import Debounce from 'utils/Debounce';
import createButton from 'utils/createButton';
import './captionToggle.scss';

/**
 * A class to expand/collapse caption text box.
 */
export default class CaptionToggle {
  /**
   * Number of lines caption text will be trucated at.
   *
   * @memberof CaptionToggle
   */
  lineLimit = 1;

  constructor(textElement, lineLimit) {
    this.caption = textElement;
    this.lineLimit = lineLimit;
    this.toggleButton = null;

    this.onResize = this.onResize.bind(this);
    this.onResizeDebounce = new Debounce(
      this.onResize,
      'resize',
      window,
      250,
      false,
    );
  }

  /**
   * Truncate caption text to line limit
   *
   * @memberof CaptionToggle
   */
  setClamp() {
    if (! this.caption || ! this.lineLimit) {
      return;
    }

    clamp(this.caption, { clamp: this.lineLimit });
  }

  /**
   * Set up the caption for toggling.
   *
   * @memberof CaptionToggle
   */
  setToggle() {
    if (! this.caption) {
      return;
    }

    this.caption.classList.add('caption-toggle');
    // hide overflow of clamp and make room for toggle button.
    this.caption.style.paddingBottom = '0.125em';
    this.caption.style.paddingRight = '2em';
    // avoid overlap with jetpack slideshow arrows.
    if (Element.prototype.closest
      && this.caption.closest('.wp-block-jetpack-slideshow')) {
      this.caption.style.maxHeight = '40%';
      this.caption.style.zIndex = '11';
    }

    const { read_more_arrow: buttonText } = nexstar.template_variables.l10n;

    if (! this.toggleButton) {
      this.toggleButton = createButton(
        'caption-toggle__button',
        `<span class="screenreadertext">${buttonText}</span>`,
        [
          {
            name: 'aria-expanded',
            value: 'false',
          },
        ],
        () => {
          this.caption.classList.toggle('caption-toggle--expanded');
          const expanded = this.toggleButton.getAttribute('aria-expanded');
          if ('false' === expanded) {
            this.toggleButton.setAttribute('aria-expanded', 'true');
          } else {
            this.toggleButton.setAttribute('aria-expanded', 'false');
          }
        },
      );

      this.caption.appendChild(this.toggleButton);
    }
  }

  /**
   * Remove the existence of caption toggle.
   */
  destroyToggle() {
    if (! this.caption || ! this.toggleButton) {
      return;
    }

    this.caption.classList.remove('caption-toggle', 'caption-toggle--expanded');
    // hide overflow of clamp and make room for toggle button.
    this.caption.style.paddingBottom = '';
    this.caption.style.paddingRight = '';
    this.toggleButton.remove();
    this.toggleButton = null;
  }

  /**
   * Determine if a toggle should be set on caption.
   *
   * @memberof CaptionToggle
   * @return boolean true when caption scoll height is greater than client height.
   */
  shouldToggle() {
    if (! this.caption) {
      return false;
    }

    const { clientHeight, scrollHeight } = this.caption;
    return Boolean(scrollHeight > clientHeight);
  }

  /**
   * Stuff to do when browser window is resized.
   */
  onResize() {
    if (this.shouldToggle() && ! this.toggleButton) {
      this.setToggle();
    } else if (! this.shouldToggle()) {
      this.destroyToggle();
    }
  }

  /**
   * Fire off caption toggle.
   */
  init() {
    this.setClamp();
    if (this.shouldToggle()) {
      this.setToggle();
    }
  }
}
